import {
  StrictMode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SetStateAction,
} from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { InteaccQueryProvider } from "@inteacc/query";
import { GridHeaderThemeProvider, type GridHeaderStyle } from "@inteacc/data-grid";
import {
  InteaccThemeProvider,
} from "@inteacc/ui";
import {
  InMemoryThemePreferenceProvider,
  defaultThemeSources,
  resolveTheme,
} from "@inteacc/theme";
import "@inteacc/design-tokens/tokens.css";
import "@inteacc/app-shell/styles.css";
import "@inteacc/application-experience/styles.css";
import "@inteacc/business-objects/styles.css";
import "@inteacc/forms/styles.css";
import "@inteacc/lookups/styles.css";
import "@inteacc/data-grid/styles.css";
import "@inteacc/screen-framework/styles.css";
import "@inteacc/visualizations/styles.css";
import "./i18n";
import "./styles.css";
import { router } from "./App";
import {
  DisplayPreferencesContext,
  type ShowcaseDensity,
  type ShowcaseTheme,
} from "./display";
import {
  createShowcasePreviewPreference,
  loadShowcaseUserPreference,
  showcaseOrganizationSources,
} from "./theme-preferences";

const HEADER_STORAGE_KEY = "iaf:showcase:grid-header:v1";
const loadGridHeader = (): GridHeaderStyle => {
  try {
    const value = window.localStorage.getItem(HEADER_STORAGE_KEY);
    return value === "accent-border" || value === "neutral" ? value : "primary-fill";
  } catch { return "primary-fill"; }
};

export const ShowcaseProviders = () => {
  const [gridHeaderStyle, setGridHeaderStyle] = useState<GridHeaderStyle>(loadGridHeader);
  useEffect(() => {
    try { window.localStorage.setItem(HEADER_STORAGE_KEY, gridHeaderStyle); }
    catch { /* preview works without browser storage */ }
  }, [gridHeaderStyle]);
  const [savedPreference, setSavedPreference] = useState(
    loadShowcaseUserPreference,
  );
  const [previewPreference, setPreviewPreference] = useState(
    createShowcasePreviewPreference,
  );
  const [provider] = useState(
    () => new InMemoryThemePreferenceProvider([savedPreference]),
  );
  const [systemTheme, setSystemTheme] = useState<ShowcaseTheme>(() =>
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  );
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  );
  const [prefersHighContrast, setPrefersHighContrast] = useState(() =>
    window.matchMedia?.("(prefers-contrast: more)").matches ?? false,
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const contrastQuery = window.matchMedia("(prefers-contrast: more)");
    const update = () => {
      setSystemTheme(darkQuery.matches ? "dark" : "light");
      setPrefersReducedMotion(motionQuery.matches);
      setPrefersHighContrast(contrastQuery.matches);
    };
    darkQuery.addEventListener?.("change", update);
    motionQuery.addEventListener?.("change", update);
    contrastQuery.addEventListener?.("change", update);
    return () => {
      darkQuery.removeEventListener?.("change", update);
      motionQuery.removeEventListener?.("change", update);
      contrastQuery.removeEventListener?.("change", update);
    };
  }, []);
  const resolvedTheme = useMemo(
    () =>
      resolveTheme({
        productId: "framework-showcase",
        tenantId: "atlas",
        userId: "showcase-user",
        screenId: "theme-studio",
        iafVersion: "0.1.0",
        environment: {
          prefersDark: systemTheme === "dark",
          prefersReducedMotion,
          prefersHighContrast,
          forcedColorsActive: false,
        },
        sources: [
          ...defaultThemeSources,
          ...showcaseOrganizationSources,
          {
            sourceId: "showcase:user:default",
            layer: "user",
            scope: savedPreference.scope,
            preference: savedPreference,
          },
          {
            sourceId: "showcase:preview",
            layer: "preview",
            scope: previewPreference.scope,
            preference: previewPreference,
          },
        ],
      }).theme,
    [
      prefersHighContrast,
      prefersReducedMotion,
      previewPreference,
      savedPreference,
      systemTheme,
    ],
  );
  const theme = resolvedTheme.mode;
  const density = resolvedTheme.values["density.mode"];
  const setDensity = useCallback((
    update: SetStateAction<ShowcaseDensity>,
  ) => {
    setPreviewPreference((current) => ({
      ...current,
      values: {
        ...current.values,
        "density.mode":
          typeof update === "function"
            ? update(
                current.values["density.mode"] ?? density,
              )
            : update,
      },
    }));
  }, [density]);
  const setTheme = useCallback((
    update: SetStateAction<ShowcaseTheme>,
  ) => {
    setPreviewPreference((current) => ({
      ...current,
      values: {
        ...current.values,
        "appearance.mode":
          typeof update === "function" ? update(theme) : update,
      },
    }));
  }, [theme]);
  const preferences = useMemo(
    () => ({
      density,
      gridHeaderStyle,
      setGridHeaderStyle,
      previewPreference,
      provider,
      resolvedTheme,
      savedPreference,
      setDensity,
      setPreviewPreference,
      setSavedPreference,
      setTheme,
      theme,
    }),
    [
      density,
      gridHeaderStyle,
      previewPreference,
      provider,
      resolvedTheme,
      savedPreference,
      setDensity,
      setTheme,
      theme,
    ],
  );
  return (
    <DisplayPreferencesContext.Provider value={preferences}>
      <InteaccQueryProvider>
        <InteaccThemeProvider resolvedTheme={resolvedTheme}>
          <GridHeaderThemeProvider value={{ style: gridHeaderStyle }}>
            <div data-showcase-theme={theme}>
              <RouterProvider router={router} />
            </div>
          </GridHeaderThemeProvider>
        </InteaccThemeProvider>
      </InteaccQueryProvider>
    </DisplayPreferencesContext.Provider>
  );
};

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Framework Showcase root is missing.");

createRoot(rootElement).render(
  <StrictMode>
    <ShowcaseProviders />
  </StrictMode>,
);
